/**
 * `ScoreCalcGuide` — guida contestuale a come viene calcolato lo **Score** di
 * una skill nel modello a due assi.
 *
 * Lo Score deriva da due assi — **Livello_Aggregato** ed **Esperienza** —
 * combinati secondo `axisWeights` (default Livello 0.75, Esperienza 0.25) e
 * rimappati nell'intervallo `[minScore, 10]` (minScore default 1). Il
 * Livello_Aggregato è la media pesata dei ranghi delle fonti di Livello presenti
 * (Self ×1, AI ×2, Reviewer ×2, Manager ×3), rinormalizzata sulle fonti
 * presenti così che le fonti assenti non abbassino il valore. La "Propensione"
 * (voto informativo di interesse) NON entra nel calcolo dello Score. Le
 * certificazioni valide collegate alla skill aggiungono invece un bonus
 * percentuale cumulativo (`certificationBonus`), che può portare lo Score oltre 10.
 *
 * Rende una piccola icona "info" che apre una `Modal` con la spiegazione,
 * riusando lo stesso stile del trigger di `LivelloGuide`. È pensato per stare
 * **dentro** l'header ordinabile di una colonna `DataTable`, il che richiede tre
 * accortezze:
 *  - il trigger è uno `<span role="button">` e non un `<button>`, per non
 *    annidare due button dentro l'header di sort (HTML non valido);
 *  - la `Modal` è renderizzata in un **portale** su `document.body`: renderla
 *    dentro la cella `<th>` la vincolerebbe alla larghezza della colonna e al
 *    clipping/stacking della tabella (da cui l'effetto "maschera" nella cella);
 *  - gli eventi del portale vengono **fermati**: con i portali React gli eventi
 *    risalgono comunque l'albero React fino al button di ordinamento, causando
 *    sort indesiderati e riaperture al click sul backdrop.
 */
import { useState, type KeyboardEvent, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Icon, Modal } from './ui';
import { useApiClient } from '../api/useApiClient';
import { DEFAULT_SCORING_CONFIG, type ScoringConfigDto } from '../shared/dto';

export interface ScoreCalcGuideProps {
  /** Classe extra sul trigger (es. per l'allineamento nell'header). */
  className?: string;
}

/** Voce della guida: chiave/peso (badge) + etichetta + descrizione. */
interface GuideEntry {
  code: string;
  label: string;
  description: string;
}

/** Formatta un peso con al più 2 decimali, senza zeri superflui. */
function fmtWeight(value: number): string {
  return Number.parseFloat(value.toFixed(2)).toString();
}

/**
 * I due assi da cui deriva lo Score, con il rispettivo peso relativo preso dalla
 * configurazione (`axisWeights`). Il badge riflette il peso realmente impostato
 * dall'amministratore; la descrizione resta generica.
 */
function buildAxisEntries(config: ScoringConfigDto): readonly GuideEntry[] {
  return [
    {
      code: fmtWeight(config.axisWeights.level),
      label: 'Livello_Aggregato',
      description:
        'Quanto la persona è brava sulla skill: media pesata dei Livelli votati dalle fonti presenti.',
    },
    {
      code: fmtWeight(config.axisWeights.experience),
      label: 'Esperienza',
      description:
        'Da quanto tempo la persona pratica la skill (fascia di anni dichiarata).',
    },
  ];
}

/**
 * Fonti di Livello e relativi pesi nel Livello_Aggregato, presi dalla
 * configurazione (`sourceWeights`) così da riflettere i valori realmente
 * impostati dall'amministratore. Le fonti assenti non entrano nella media
 * (rinormalizzazione sulle fonti presenti).
 */
function buildSourceEntries(config: ScoringConfigDto): readonly GuideEntry[] {
  const { sourceWeights } = config;
  return [
    {
      code: `×${fmtWeight(sourceWeights.self)}`,
      label: 'Self',
      description: 'Autovalutazione della persona: la fonte più soggettiva.',
    },
    {
      code: `×${fmtWeight(sourceWeights.ai)}`,
      label: 'AI',
      description: 'Livello stimato dal quiz AI (AIgor), quando disponibile.',
    },
    {
      code: `×${fmtWeight(sourceWeights.reviewer)}`,
      label: 'Reviewer',
      description: 'Media dei Livelli votati dai reviewer che hanno valutato la persona sulla skill.',
    },
    {
      code: `×${fmtWeight(sourceWeights.manager)}`,
      label: 'Manager',
      description: 'Media dei Livelli votati dai manager sulla skill.',
    },
  ];
}

/**
 * Icona "info" + modale con la spiegazione dello Score. Stato di apertura
 * interno; il chiamante posiziona solo il componente (es. nell'header della
 * colonna "Score").
 */
export function ScoreCalcGuide({ className }: ScoreCalcGuideProps) {
  const [open, setOpen] = useState(false);
  const api = useApiClient();

  // Configurazione di scoring: i pesi mostrati nella guida sono quelli
  // realmente impostati dall'amministratore. In attesa/errore ripiega sui
  // default applicativi.
  const { data: config = DEFAULT_SCORING_CONFIG } = useQuery({
    queryKey: ['settings', 'scoring'],
    queryFn: () => api.get<ScoringConfigDto>('/api/settings/scoring'),
    staleTime: 5 * 60 * 1000,
  });

  const axisEntries = buildAxisEntries(config);
  const sourceEntries = buildSourceEntries(config);

  const triggerClasses = ['livello-guide__trigger', 'score-calc-guide__trigger', className ?? '']
    .filter(Boolean)
    .join(' ');

  // Apre la modale senza far scattare l'ordinamento della colonna (il trigger
  // vive dentro il button di sort dell'header).
  const openGuide = (e: MouseEvent | KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') openGuide(e);
  };

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        className={triggerClasses}
        aria-label="Come si calcola lo Score"
        title="Come si calcola lo Score"
        onClick={openGuide}
        onKeyDown={onKeyDown}
      >
        <Icon name="info" size={16} />
      </span>

      {open &&
        createPortal(
          // Il wrapper isola il modale dall'header di sort che lo contiene
          // nell'albero React: senza questo, click/tasti dentro la modale
          // (incluso il backdrop) attiverebbero l'ordinamento della colonna.
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Modal
              open
              size="wide"
              onClose={() => setOpen(false)}
              title="Come si calcola lo Score"
              footer={
                <Button type="button" variant="primary" onClick={() => setOpen(false)}>
                  Ho capito
                </Button>
              }
            >
              <p className="livello-guide__intro">
                Lo Score nasce da <strong>due assi</strong> —{' '}
                <strong>Livello_Aggregato</strong> ed <strong>Esperienza</strong> —
                combinati con i pesi qui sotto e poi rimappati nell’intervallo{' '}
                <strong>da {fmtWeight(config.minScore)} a 10</strong>: il minimo
                ({fmtWeight(config.minScore)}) corrisponde a Livello <em>e-1</em>{' '}
                con meno di 1 anno di esperienza, il massimo (10) a Livello{' '}
                <em>e-5</em> con oltre 10 anni. La{' '}
                <strong>Propensione</strong> (il voto informativo di interesse){' '}
                <strong>non</strong> entra nel calcolo dello Score.
              </p>

              <p className="livello-guide__intro">
                <strong>Gli assi e il loro peso</strong> (impostati
                dall’amministratore):
              </p>
              <ul className="livello-guide__list score-calc-guide__list">
                {axisEntries.map((entry) => (
                  <li key={entry.label} className="livello-guide__item score-calc-guide__item">
                    <span className="livello-guide__code">{entry.code}</span>
                    <span className="livello-guide__label">{entry.label}</span>
                    <span className="livello-guide__desc">{entry.description}</span>
                  </li>
                ))}
              </ul>

              <p className="livello-guide__intro score-calc-guide__intro">
                Il <strong>Livello_Aggregato</strong> è la media{' '}
                <strong>pesata</strong> dei Livelli votati dalle fonti{' '}
                <strong>presenti</strong>. Ogni fonte pesa secondo la
                configurazione impostata dall’amministratore; le fonti assenti
                non entrano nel calcolo (non abbassano la media):
              </p>
              <ul className="livello-guide__list score-calc-guide__list">
                {sourceEntries.map((entry) => (
                  <li key={entry.label} className="livello-guide__item score-calc-guide__item">
                    <span className="livello-guide__code">{entry.code}</span>
                    <span className="livello-guide__label">{entry.label}</span>
                    <span className="livello-guide__desc">{entry.description}</span>
                  </li>
                ))}
              </ul>

              <p className="livello-guide__intro score-calc-guide__intro">
                <strong>Bonus certificazioni.</strong>{' '}
                {config.certificationBonus > 0 ? (
                  <>
                    Ogni <strong>certificazione valida</strong> (non scaduta)
                    collegata alla skill aumenta lo Score del{' '}
                    <strong>{fmtWeight(config.certificationBonus)}%</strong>, in modo
                    cumulativo. Il bonus può portare lo Score{' '}
                    <strong>oltre 10</strong>: in tal caso è mostrato come «10+» in
                    oro, con un badge (coccarda + numero) accanto allo Score che
                    indica quante certificazioni contribuiscono.
                  </>
                ) : (
                  <>
                    Quando configurato dall’amministratore, ogni certificazione
                    valida collegata alla skill aumenta lo Score di una percentuale
                    (cumulativa), potenzialmente oltre 10 («10+»). Attualmente il
                    bonus è disattivato.
                  </>
                )}
              </p>
            </Modal>
          </div>,
          document.body,
        )}
    </>
  );
}

export default ScoreCalcGuide;
